import { useCallback, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { LogOut, Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { fetchSyncStatus, SYNC_STATUS_CHANGED_EVENT } from '@/api/sync'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

function formatSyncTime(iso) {
  if (!iso) return null
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function syncFailureHint(lastSync) {
  const errors = Array.isArray(lastSync?.errors) ? lastSync.errors : []
  const fatal = errors.find((entry) => entry?.fatal)?.fatal
  if (fatal) return String(fatal).trim()
  if (errors.length > 0) {
    return `${errors.length} error(s) in the last Atera sync`
  }
  return 'Last Atera sync failed'
}

export function TopBar({
  title,
  subtitle,
  icon: Icon,
  onMenuClick,
  tvMode = false,
  onToggleTvMode,
  forceMenuButton = false,
}) {
  const { user, logout, isAdmin } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [syncInfo, setSyncInfo] = useState(null)

  const refreshSyncStatus = useCallback(() => {
    if (!isAdmin) return
    fetchSyncStatus()
      .then(setSyncInfo)
      .catch(() => setSyncInfo(null))
  }, [isAdmin])

  useEffect(() => {
    refreshSyncStatus()
  }, [refreshSyncStatus, location.pathname])

  useEffect(() => {
    if (!isAdmin) return undefined

    const onFocus = () => refreshSyncStatus()
    const onSyncChanged = () => refreshSyncStatus()
    const intervalId = window.setInterval(refreshSyncStatus, 30000)

    window.addEventListener('focus', onFocus)
    window.addEventListener(SYNC_STATUS_CHANGED_EVENT, onSyncChanged)

    return () => {
      window.clearInterval(intervalId)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener(SYNC_STATUS_CHANGED_EVENT, onSyncChanged)
    }
  }, [isAdmin, refreshSyncStatus])

  async function handleLogout() {
    await logout()
    navigate('/login', { replace: true })
  }

  const roleLabel =
    user?.role === 'admin'
      ? 'Administrator'
      : user?.role === 'staff'
        ? 'Staff'
        : 'Customer'

  const lastSync = syncInfo?.last_sync
  const syncBadge =
    syncInfo?.running
      ? { label: 'Sync running', variant: 'warning', title: 'Atera sync is in progress' }
      : lastSync?.status === 'success'
        ? {
            label: `Synced ${formatSyncTime(lastSync.completed_at || lastSync.started_at)}`,
            variant: 'success',
            title: 'Last Atera sync succeeded',
          }
        : lastSync?.status === 'failed'
          ? {
              label: 'Sync failed',
              variant: 'destructive',
              title: syncFailureHint(lastSync),
            }
          : null

  return (
    <header className="grid h-16 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 border-b border-[#d8d8d8] bg-[#efefef] px-4 shadow-sm sm:px-6">
      <div className="flex min-w-0 items-center justify-start gap-2">
        {onMenuClick ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={forceMenuButton ? 'shrink-0' : 'shrink-0 lg:hidden'}
            onClick={onMenuClick}
            aria-label="Open navigation"
          >
            <Menu className="h-4 w-4" />
          </Button>
        ) : null}
      </div>

      <div className="flex max-w-[min(100vw-12rem,36rem)] flex-col items-center justify-center px-2 text-center">
        <h1 className="flex max-w-full items-center justify-center gap-2 text-lg font-semibold tracking-tight sm:text-xl">
          {Icon ? <Icon className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" /> : null}
          <span className="truncate">{title}</span>
        </h1>
        {subtitle ? (
          <p className="max-w-full truncate text-sm text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>

      <div className="flex min-w-0 items-center justify-end gap-2 sm:gap-3">
        {onToggleTvMode ? (
          <Button
            type="button"
            variant={tvMode ? 'default' : 'outline'}
            size="sm"
            onClick={onToggleTvMode}
            title={
              tvMode
                ? 'Exit TV view — show navigation'
                : 'TV view — hide navigation and go fullscreen'
            }
            aria-label={
              tvMode
                ? 'Exit TV view — show navigation'
                : 'TV view — hide navigation and go fullscreen'
            }
            aria-pressed={tvMode}
          >
            {tvMode ? (
              <PanelLeftOpen className="h-4 w-4" />
            ) : (
              <PanelLeftClose className="h-4 w-4" />
            )}
            <span className="hidden sm:inline">{tvMode ? 'Exit TV' : 'TV view'}</span>
          </Button>
        ) : null}
        {syncBadge && (
          <Link
            to="/admin/sync"
            title={syncBadge.title}
            className="hidden md:inline-flex"
          >
            <Badge variant={syncBadge.variant}>{syncBadge.label}</Badge>
          </Link>
        )}
        <Badge variant="secondary" className="hidden sm:inline-flex">
          {roleLabel}
        </Badge>
        <span className="hidden max-w-[10rem] truncate text-sm text-muted-foreground lg:inline">
          {user?.full_name}
        </span>
        <Button variant="outline" size="sm" onClick={handleLogout}>
          <LogOut className="h-4 w-4" />
          <span className="hidden sm:inline">Sign out</span>
        </Button>
      </div>
    </header>
  )
}
